import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

// Studio's full view of the authenticated user, resolved from Studio's DB.
// Unlike the JWT-parsed SessionUser (identity only), this includes
// Studio-resolved tenant context (tenantId, roleName) and profile fields.
const currentProfileSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  fullName: z.string().nullable(),
  tenantId: z.string().nullable(),
  roleName: z.string().nullable(),
  isSuperAdmin: z.boolean(),
  onboardingCompleted: z.boolean(),
  isEmailVerified: z.boolean(),
});

export type CurrentProfile = z.infer<typeof currentProfileSchema>;

// Shared hook — safe to call from multiple components (TanStack dedupes).
// Syncs tenantStore with the DB-resolved tenantId so the X-Tenant-Id header
// interceptor and useMyPermissions see the correct tenant even when the JWT
// carries no tenant_id claim (invited members, cross-platform auth).
export function useCurrentProfile() {
  const result = useQuery({
    queryKey: ['currentProfile'],
    queryFn: async ({ signal }) => {
      const raw = await apiClient.get('api/v1/me', { signal }).json();
      return currentProfileSchema.parse(raw);
    },
    staleTime: 60_000,
  });

  const storedTenantId = useTenantStore((s) => s.tenantId);

  useEffect(() => {
    const resolved = result.data?.tenantId ?? null;
    if (resolved !== storedTenantId) {
      useTenantStore.getState().setTenantId(resolved);
    }
  }, [result.data?.tenantId, storedTenantId]);

  return result;
}
